import { Fragment, useEffect, useState } from 'react';

import { MegaMenu } from 'primereact/megamenu';

import { translate, translateError } from '../language';

import { ConfirmDialogService } from '../../AppConfirmDialog';
import { ToastService } from '../../AppToast';
import { AppStorage } from '../../service/UserStorage';
import { useTranslation } from '../language';

const localizeActions = (actions: any[]): any[] => (actions || []).map((action) => {
  if (Array.isArray(action)) {
    return localizeActions(action);
  }

  return {
    ...action,
    label: typeof action.label === 'string' ? translate(action.label) : action.label,
    title: typeof action.title === 'string' ? translate(action.title) : action.title,
    items: Array.isArray(action.items) ? localizeActions(action.items) : action.items,
  };
});

interface ToolbarProps {

  dataKey?: string;

  createRole?: string;

  editRole?: string;

  cloneRole?: string;

  deleteRole?: string;

  importRole?: string;

  exportRole?: string;

  create?(param?: any): void;

  createParam?: any;

  update?(item: any): void;

  clone?(item: any): void;

  deleteItem?(id: string, item: any): Promise<any>;

  deleteItems?(ids: Array<any>, items: Array<any>): Promise<any>;

  validateDeleteItems?(items: any): boolean;

  reloadLazyData?(): void;

  selectedItems?: Array<any>;

  showImportDialog?(): void;

  exportExcel?(params: any): void;

  dataTableParams?: any;

  otherActions?: Array<any>;
  functionActions?: Array<any>;

  endMenu?: any;

  style?: React.CSSProperties
}

export const useToolbar = (props: ToolbarProps) => {
  const { t, i18n } = useTranslation();
  let permissionString = AppStorage.get('pk');
  let permissions = JSON.parse(permissionString) || [];

  const { dataKey, create, createParam, update, clone, deleteItem, deleteItems, validateDeleteItems, reloadLazyData, selectedItems, showImportDialog, exportExcel, dataTableParams, otherActions,
    functionActions, createRole, editRole, cloneRole, deleteRole, importRole, exportRole, endMenu } = props;

  const [actions, setActions] = useState([]);
  const [toolbarState, setToolbarState] = useState(0);

  const refreshToolbar = () => {
    setToolbarState(Date.now);
  }

  useEffect(() => {
    return () => {
      setActions([]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    let _create;
    if (createParam) {
      _create = () => {
        create(createParam);
      }
    } else {
      _create = create;
    }

    let hasItems = false, selectedItem;
    if (Array.isArray(selectedItems)) {
      let totalItems = selectedItems ? selectedItems.length : 0;
      if (totalItems === 1) {
        selectedItem = selectedItems[0];
      }
      if (totalItems > 0) {
        hasItems = true;
      }
    } else if (selectedItems && Object.keys(selectedItems)) {
      selectedItem = selectedItems;
      hasItems = true;
    }

    let _update;
    if (selectedItem) {
      _update = () => {
        update(selectedItem);
      }
    }
    const update_disabled = !selectedItem;

    let _clone;
    if (clone && selectedItem) {
      _clone = () => {
        clone(selectedItem);
      }
    }
    const clone_disabled = !selectedItem;

    const _actions: any = [];
    if (reloadLazyData) {
      _actions.push({
        label: t('common.refresh'), icon: 'pi pi-fw pi-refresh', className: 'iam-toolbar iam-toolbar-secondary', command: reloadLazyData, disabled: !reloadLazyData
      });
    }
    if (_create && (!createRole || permissions.includes(createRole))) {
      _actions.push({
        label: `${t('common.create')} (F4)`, icon: 'pi pi-fw pi-plus', className: 'iam-toolbar', command: _create
      });
    }
    if (update && (!editRole || permissions.includes(editRole))) {
      _actions.push({
        label: `${t('common.edit')} (F3)`, icon: 'pi pi-fw pi-pencil', className: 'iam-toolbar iam-toolbar-info', command: _update, disabled: update_disabled
      });
    }
    if ((deleteItem || deleteItems) && (!deleteRole || permissions.includes(deleteRole))) {
      _actions.push({
        label: `${t('common.delete')} (F8)`, icon: 'pi pi-fw pi-trash', className: 'iam-toolbar iam-toolbar-danger', command: () => {
          _deleteItems(selectedItems);
        }, disabled: !hasItems
      });
    }
    if (clone && (!cloneRole || permissions.includes(cloneRole))) {
      _actions.push({
        label: t('common.copy'), icon: 'pi pi-fw pi-copy', className: 'iam-toolbar iam-toolbar-success', command: _clone, disabled: clone_disabled
      });
    }
    if (showImportDialog && (!importRole || permissions.includes(importRole))) {
      _actions.push({
        label: t('common.importExcel'), icon: 'pi pi-fw pi-upload', className: 'iam-toolbar iam-toolbar-help', command: showImportDialog
      });
    }
    if (exportExcel && (!exportRole || permissions.includes(exportRole))) {
      _actions.push({
        label: t('common.exportExcel'), icon: 'pi pi-fw pi-file-excel', className: 'iam-toolbar iam-toolbar-help', command: () => {
          exportExcel(dataTableParams);
        }
      });
    }
    if (otherActions && otherActions.length) {
      _actions.push(...localizeActions(otherActions));
    }

    if (functionActions && functionActions.length) {
      _actions.push(...localizeActions(functionActions));
    }

    setActions(_actions);
    const onKeydown = event => {
      if (window['preventToolbarShortcut'] !== true) {
        if (event && event.which) {
          switch (event.which) {
            case 114:
              if (!update_disabled && (!editRole || permissions.includes(editRole))) {
                _update();
              }
              event.preventDefault();
              break;
            case 115:
              if (!createRole || permissions.includes(createRole)) {
                create();
              }

              event.preventDefault();
              break;
            case 119:
              if (hasItems && (!deleteRole || permissions.includes(deleteRole))) {
                _deleteItems(selectedItems);
              }
              event.preventDefault();
              break;
          }
        }
      }
    }
    window.addEventListener('keydown', onKeydown);
    return () => {
      window.removeEventListener('keydown', onKeydown);
    };
  }, [createParam, selectedItems, dataTableParams, toolbarState, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const _deleteItems = (items) => {
    if (items && (!validateDeleteItems || validateDeleteItems(items))) {
      ConfirmDialogService.confirmDelete(() => {
        const complete = [];
        if (Array.isArray(items)) {
          if (deleteItems) {
            complete.push(new Promise<void>((resolve, reject) => {
              deleteItems(items.map(item => item[dataKey]), items).then(() => {
                resolve();
              }).catch(({ response }) => {
                reject();
                ToastService.error(translateError(response));
              });
            }));
          } else {
            items.forEach((item: any) => {
              complete.push(new Promise<void>((resolve, reject) => {
                deleteItem(item[dataKey], item).then(() => {
                  resolve();
                }).catch((error) => {
                  reject();
                  ToastService.error(error);
                });
              }));
            });
          }
        } else {
          complete.push(new Promise<void>((resolve, reject) => {
            deleteItem(items[dataKey], items).then(() => {
              resolve();
            }).catch(({ response }) => {
              reject();
              ToastService.error(translateError(response));
            });
          }));
        }
        Promise.all(complete).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      });
    }
  }

  const renderToolbar = () => {
    return (
      <Fragment>
        <MegaMenu style={props.style || {}} model={actions} end={endMenu} />
      </Fragment>
    );
  }

  return { renderToolbar, setActions, refreshToolbar };
}
